import { DAM_DECK } from './damDeck';
import { WORLD } from '../core/config';
import type { Layout } from '../data/types';
import type { LandHeight } from './types';
import type { Vec2 } from '../geo/types';
import { distancePointSegment, pointInRing, ringBounds } from '../geo/polygon';
import { fitChurchGround } from './churchGround';

/** Local reconstruction controls, in layout metres. These are editable modelling estimates,
 * not surveyed 1538 levels. A flat district core blends into surrounding streets. */
export interface GroundLevel {
  readonly at: Vec2;
  readonly height: number;
  readonly radius: number;
  readonly transition: number;
}

export const DAM_SURROUNDINGS: readonly GroundLevel[] = [
  { at: [60, -30], height: DAM_DECK.crestY, radius: 110, transition: 300 },
  { at: [60, -240], height: 2.6, radius: 75, transition: 100 },
];

/**
 * PLAN Ground, the banks (2026-10-06): the painting draws the Damrak's and the Rokin's quays low,
 * boats moored at bank level, while the Dam stands high between them. The raised ground about the
 * Dam therefore falls to `quayY` at a canal's or the Damrak's bank, over `reach` m behind it or
 * the length a mean `grade` needs, except within `hold` m of the dam, whose abutments carry the
 * crest to the water, blending in over `blend` m beyond that. Levels are modelling estimates, not
 * surveyed 1538 elevations.
 */
export const BANK_FALL = { quayY: 0.6, reach: 40, grade: 0.04, hold: 20, blend: 40 } as const;
/* The water with quays: a ditch lies in the ground and the wall stands on the moat's bank. */
const QUAYED: ReadonlySet<string> = new Set(['canal', 'river', 'harbour']);

const smooth = (t: number) => t * t * (3 - 2 * t);
const clamp = (t: number) => Math.max(0, Math.min(1, t));

interface BankEdge {
  readonly a: Vec2;
  readonly b: Vec2;
  /** Half the water's width about the edge: zero for a bank, the channel's for its centreline. */
  readonly half: number;
}

/* The water's edges that can lower ground inside `box`: the quayed channels' centrelines with their
   widths and the Damrak's bank, each kept with a box of its reach. */
function bankEdges(
  layout: Layout,
  box: { minX: number; maxX: number; minY: number; maxY: number }
) {
  const edges: (BankEdge & { minX: number; maxX: number; minY: number; maxY: number })[] = [];
  const keep = (a: Vec2, b: Vec2, half: number) => {
    const reach =
      half + Math.max(BANK_FALL.reach, (DAM_DECK.crestY - BANK_FALL.quayY) / BANK_FALL.grade);
    const e = {
      a,
      b,
      half,
      minX: Math.min(a[0], b[0]) - reach,
      maxX: Math.max(a[0], b[0]) + reach,
      minY: Math.min(a[1], b[1]) - reach,
      maxY: Math.max(a[1], b[1]) + reach,
    };
    if (e.maxX < box.minX || e.minX > box.maxX || e.maxY < box.minY || e.minY > box.maxY) return;
    edges.push(e);
  };
  for (const channel of layout.water.channels.filter((c) => QUAYED.has(c.kind)))
    channel.centerline.forEach((a, i) => {
      const b = channel.centerline[i + 1];
      if (b) keep(a, b, (channel.widths[i]! + channel.widths[i + 1]!) / 4);
    });
  for (const area of layout.water.areas)
    area.polygon.outer.forEach((a, i) => {
      keep(a, area.polygon.outer[(i + 1) % area.polygon.outer.length]!, 0);
    });
  return edges;
}

/** Foundation and bank level, before paving crowns, bridge arches and water stairs.
 * The Dam's adjoining ground stays at its crest, then falls gently over the neighbourhood.
 * Keep this separate from WORLD.groundY, the reference plane used by painter projection. */
export function createGroundElevation(
  layout: Layout,
  levels: readonly GroundLevel[] = DAM_SURROUNDINGS
): LandHeight {
  const dams = layout.landmarks.filter((l) => l.kind === 'dam');
  // Synthetic layouts without the Dam retain their explicitly flat reference ground.
  if (!dams.length) return () => WORLD.groundY;
  const preparedLevels = levels.map((level) => ({
    ...level,
    innerSq: level.radius ** 2,
    outerSq: (level.radius + level.transition) ** 2,
  }));
  const preparedDams = dams.map((dam) => {
    const b = ringBounds(dam.footprint);
    return {
      footprint: dam.footprint,
      box: { minX: b.minX - 145, maxX: b.maxX + 145, minY: b.minY - 145, maxY: b.maxY + 145 },
      edges: dam.footprint.map((a, i) => ({
        a,
        b: dam.footprint[(i + 1) % dam.footprint.length]!,
      })),
    };
  });
  const boxes = [
    ...levels.map((l) => ({
      minX: l.at[0] - l.radius - l.transition,
      maxX: l.at[0] + l.radius + l.transition,
      minY: l.at[1] - l.radius - l.transition,
      maxY: l.at[1] + l.radius + l.transition,
    })),
    ...preparedDams.map((dam) => dam.box),
  ];
  const elevationBounds = {
    minX: Math.min(...boxes.map((b) => b.minX)),
    maxX: Math.max(...boxes.map((b) => b.maxX)),
    minY: Math.min(...boxes.map((b) => b.minY)),
    maxY: Math.max(...boxes.map((b) => b.maxY)),
  };
  const banks = bankEdges(layout, elevationBounds);
  const bankDistance = (p: Vec2, reach: number): number => {
    let distance = reach;
    for (const e of banks) {
      if (p[0] < e.minX || p[0] > e.maxX || p[1] < e.minY || p[1] > e.maxY) continue;
      distance = Math.min(distance, Math.max(0, distancePointSegment(p, e) - e.half));
    }
    return distance;
  };
  const elevation = Object.assign(
    (p: Vec2) => {
      if (
        p[0] < elevationBounds.minX ||
        p[0] > elevationBounds.maxX ||
        p[1] < elevationBounds.minY ||
        p[1] > elevationBounds.maxY
      )
        return WORLD.groundY;
      let height: number = WORLD.groundY;
      let damDistance = Infinity;
      for (const level of preparedLevels) {
        const dx = p[0] - level.at[0],
          dy = p[1] - level.at[1],
          distanceSq = dx * dx + dy * dy;
        if (distanceSq >= level.outerSq) continue;
        const weight =
          distanceSq <= level.innerSq
            ? 1
            : 1 - smooth(clamp((Math.hypot(dx, dy) - level.radius) / level.transition));
        height += (level.height - height) * weight;
      }
      for (const dam of preparedDams) {
        const b = dam.box;
        if (p[0] < b.minX || p[0] > b.maxX || p[1] < b.minY || p[1] > b.maxY) continue;
        let distance = 0;
        if (!pointInRing(p, dam.footprint)) {
          distance = Infinity;
          for (const edge of dam.edges)
            distance = Math.min(distance, distancePointSegment(p, edge));
        }
        // A broad level forecourt replaces the old ten-metre ramps at the Dam ends.
        const weight = 1 - smooth(clamp((distance - 45) / 100));
        height += (DAM_DECK.crestY - height) * weight;
        damDistance = Math.min(damDistance, distance);
      }
      if (height <= BANK_FALL.quayY) return height;
      const fall = smooth(clamp((damDistance - BANK_FALL.hold) / BANK_FALL.blend));
      if (fall === 0) return height;
      const drop = height - BANK_FALL.quayY;
      const reach = Math.max(BANK_FALL.reach, drop / BANK_FALL.grade);
      const atBank = BANK_FALL.quayY + drop * smooth(bankDistance(p, reach) / reach);
      return height + (atBank - height) * fall;
    },
    { elevationBounds }
  );
  return fitChurchGround(layout, elevation);
}
